import { useCallback, useEffect, useState, Fragment } from 'react';
import { getAcFilters, logAcFilterChange } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Wind, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  overdue:  { label: (d) => `Overdue ${Math.abs(d)}d`, cls: 'bg-red-50 text-red-700 border-red-200' },
  due_soon: { label: (d) => `Due in ${d}d`,            cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  good:     { label: (d) => `Good — ${d}d`,            cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  no_data:  { label: () => 'No data',                   cls: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

const fmtDateShort = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
};

function SummaryCard({ label, value, valueClass, testid }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card px-5 py-4" data-testid={testid}>
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className={`mt-2 text-4xl font-semibold tabular-nums ${valueClass}`}>{value}</p>
    </div>
  );
}

function StatusPill({ status, days_until }) {
  const meta = STATUS_META[status] || STATUS_META.no_data;
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
      {meta.label(days_until)}
    </span>
  );
}

function LogChangeDialog({ open, onOpenChange, unit, onSaved }) {
  const [date, setDate] = useState('');
  const [size, setSize] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && unit) {
      setDate(new Date().toISOString().slice(0, 10));
      setSize(unit.filter_size || '');
    }
  }, [open, unit]);

  const save = async () => {
    if (!date) { toast.error('Pick a date'); return; }
    setSaving(true);
    try {
      await logAcFilterChange({ unit_id: unit.id, date_changed: date, filter_size: size || null });
      toast.success('Filter change logged');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log change');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ac-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Wind className="h-5 w-5 text-primary" /> Log AC Filter Change
          </DialogTitle>
          <DialogDescription>
            Unit {unit?.unit_number} — updates the filter cycle and reminder schedule.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Date Changed</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="ac-log-date" />
          </div>
          <div className="space-y-2">
            <Label>Filter Size (optional)</Label>
            <Input value={size} onChange={e => setSize(e.target.value)} placeholder="e.g. 16x25x1" data-testid="ac-log-size" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="ac-log-save">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PropertyTable({ group, onLogChange, expanded, onToggle, isFirst }) {
  const dueCount = group.units.filter(u => u.status === 'overdue' || u.status === 'due_soon').length;
  return (
    <section className={`space-y-2 ${isFirst ? '' : 'mt-6'}`} data-testid={`ac-property-group-${group.property_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex flex-wrap items-center gap-2 text-left hover:text-foreground/80 transition-colors"
        data-testid={`ac-property-toggle-${group.property_id}`}
      >
        {expanded
          ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
        <h3 className="font-heading text-sm font-semibold tracking-tight">
          {group.property_name}
          {group.property_address && <span className="text-muted-foreground font-normal"> — {group.property_address}</span>}
        </h3>
        <span className="text-[11px] text-muted-foreground">({group.units.length} units{dueCount > 0 ? `, ${dueCount} due` : ''})</span>
        {group.trip_consolidation_count >= 2 && (
          <span
            className="inline-flex items-center rounded-md bg-sky-50 border border-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-800"
            data-testid={`ac-trip-badge-${group.property_id}`}
          >
            Consolidate trip — {group.trip_consolidation_count} filters due
          </span>
        )}
      </button>

      {expanded && (
        <Card className="overflow-hidden border-border/60">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="text-left px-4 py-2 w-[130px]">Unit</th>
                  <th className="text-left px-4 py-2 w-[140px]">Filter Size</th>
                  <th className="text-left px-4 py-2 w-[140px]">Last Changed</th>
                  <th className="text-left px-4 py-2 w-[140px]">Next Due</th>
                  <th className="text-left px-4 py-2 w-[130px]">Status</th>
                  <th className="text-left px-4 py-2">Flags</th>
                  <th className="text-right px-4 py-2 w-[130px]"></th>
                </tr>
              </thead>
              <tbody>
                {group.units.map((u, idx) => (
                  <Fragment key={u.id}>
                    <tr
                      className={idx > 0 ? 'border-t border-border/50' : ''}
                      data-testid={`ac-unit-row-${u.id}`}
                    >
                      <td className="px-4 py-2 font-medium">Unit {u.unit_number}</td>
                      <td className="px-4 py-2 text-muted-foreground">{u.filter_size || <span className="text-muted-foreground/60">—</span>}</td>
                      <td className="px-4 py-2 text-muted-foreground">{fmtDate(u.filter_last_changed)}</td>
                      <td className="px-4 py-2 text-muted-foreground">{fmtDate(u.next_due)}</td>
                      <td className="px-4 py-2">
                        <StatusPill status={u.status} days_until={u.days_until} />
                      </td>
                      <td className="px-4 py-2">
                        {u.pre_move_in_flag && (
                          <span
                            className="inline-flex items-center rounded-md bg-sky-50 border border-sky-200 px-2 py-0.5 text-xs font-medium text-sky-800"
                            data-testid={`ac-premovein-${u.id}`}
                          >
                            Replace before move-in
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => onLogChange(u)}
                          data-testid={`ac-log-btn-${u.id}`}
                        >
                          Log change
                        </Button>
                      </td>
                    </tr>
                    {u.pre_move_in_flag && u.pre_move_in_date && (
                      <tr className="bg-muted/10">
                        <td className="px-4 pb-2 pt-0 text-xs italic text-muted-foreground" colSpan={7}>
                          Move-in {fmtDateShort(u.pre_move_in_date)} — within change window, replace before move-in
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </section>
  );
}

export default function ACFiltersTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logDialog, setLogDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await getAcFilters();
      setData(res);
    } catch {
      toast.error('Failed to load AC filters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="py-12 text-center text-muted-foreground text-sm">Loading...</div>;
  if (!data) return null;

  const s = data.summary || {};
  const openLog = (unit) => { setSelectedUnit(unit); setLogDialog(true); };
  const toggle = (pid) => setCollapsed(prev => ({ ...prev, [pid]: !prev[pid] }));
  const toggleAll = () => {
    const allCollapsed = (data.groups || []).every(g => collapsed[g.property_id]);
    const next = {};
    (data.groups || []).forEach(g => { next[g.property_id] = !allCollapsed; });
    setCollapsed(next);
  };

  return (
    <div className="space-y-5" data-testid="ac-filters-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Overdue" value={s.overdue || 0} valueClass="text-red-700" testid="ac-summary-overdue" />
        <SummaryCard label="Due this month" value={s.due_soon || 0} valueClass="text-amber-700" testid="ac-summary-due-soon" />
        <SummaryCard label="Pre-move-in flags" value={s.pre_move_in || 0} valueClass="text-indigo-700" testid="ac-summary-premovein" />
        <SummaryCard label="All good" value={s.good || 0} valueClass="text-emerald-700" testid="ac-summary-good" />
      </div>

      {(data.groups || []).length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={toggleAll}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            data-testid="ac-toggle-all"
          >
            {(data.groups || []).every(g => collapsed[g.property_id]) ? 'Expand all' : 'Collapse all'}
          </button>
        </div>
      )}

      {/* Groups */}
      {(data.groups || []).length === 0 ? (
        <Card className="border-dashed">
          <div className="py-12 text-center text-muted-foreground text-sm">No units found.</div>
        </Card>
      ) : (
        <div>
          {data.groups.map((g, i) => (
            <PropertyTable
              key={g.property_id}
              group={g}
              onLogChange={openLog}
              expanded={!collapsed[g.property_id]}
              onToggle={() => toggle(g.property_id)}
              isFirst={i === 0}
            />
          ))}
        </div>
      )}

      <LogChangeDialog
        open={logDialog}
        onOpenChange={setLogDialog}
        unit={selectedUnit}
        onSaved={load}
      />
    </div>
  );
}
